import React, { useState, useCallback, useRef, useEffect } from 'react'
import { View, StyleSheet, Modal, TouchableOpacity, FlatList, TextInput, Platform } from 'react-native'
import { useTheme, useNavigation } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { Icon, Text } from '../atoms'
import { api } from '@api'
import { Contact } from '../../hooks/api/queries/types/contactTypes'
import { Conversation } from '../../hooks/api/queries/useGetConversationById'
import { Category } from '../../hooks/api/queries/usePaginateCategories'
import { Hashtag } from '../../hooks/api/queries/usePaginateHashtags'

type SearchResultType = 'contact' | 'conversation' | 'category' | 'hashtag'

type SearchResult = {
  id: string
  type: SearchResultType
  title: string
  subtitle?: string
  avatarUrl?: string | null
}

interface GlobalSearchModalProps {
  visible: boolean
  onClose: () => void
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ visible, onClose }) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const navigation = useNavigation()
  const { t } = useLanguage()
  const inputRef = useRef<TextInput>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      setQuery('')
      setResults([])
    }
  }, [visible])

  const search = useCallback(async (text: string) => {
    if (text.length < 2) {
      setResults([])
      return
    }

    setLoading(true)
    try {
      const [contactsRes, conversationsRes, categoriesRes, hashtagsRes] = await Promise.allSettled([
        api.get<{ data: Contact[] }>(`/contacts?search=${encodeURIComponent(text)}&limit=5`),
        api.get<{ data: Conversation[] }>(`/conversations?search=${encodeURIComponent(text)}&limit=5`),
        api.get<{ data: Category[] }>(`/categories?q=${encodeURIComponent(text)}&limit=5`),
        api.get<{ data: Hashtag[] }>(`/hashtags?q=${encodeURIComponent(text)}&limit=5`),
      ])

      const mapped: SearchResult[] = []

      if (contactsRes.status === 'fulfilled') {
        contactsRes.value.data.data.forEach((c) => {
          mapped.push({
            id: c.id,
            type: 'contact',
            title: c.fullName,
            subtitle: c.professions?.[0]?.name,
            avatarUrl: c.avatarUrl,
          })
        })
      }

      if (conversationsRes.status === 'fulfilled') {
        conversationsRes.value.data.data.forEach((c) => {
          const name = c.name || c.participants.map(p => p.fullName).filter(Boolean).join(', ')
          mapped.push({
            id: c.id,
            type: 'conversation',
            title: name || 'Conversation',
            subtitle: c.lastMessage?.content,
          })
        })
      }

      if (categoriesRes.status === 'fulfilled') {
        categoriesRes.value.data.data.forEach((c) => {
          mapped.push({
            id: c.id,
            type: 'category',
            title: c.name,
            avatarUrl: c.avatarUrl,
          })
        })
      }

      if (hashtagsRes.status === 'fulfilled') {
        hashtagsRes.value.data.data.forEach((h) => {
          mapped.push({
            id: h.id,
            type: 'hashtag',
            title: `#${h.name}`,
          })
        })
      }

      setResults(mapped)
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [])

  const onChangeText = useCallback((text: string) => {
    setQuery(text)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(text), 300)
  }, [search])

  const getIconForType = (type: SearchResultType) => {
    switch (type) {
      case 'contact': return 'people'
      case 'conversation': return 'messageCircle'
      case 'category': return 'category'
      case 'hashtag': return 'htag'
    }
  }

  const getLabelForType = (type: SearchResultType) => {
    switch (type) {
      case 'contact': return 'Contact'
      case 'conversation': return 'Message'
      case 'category': return 'Category'
      case 'hashtag': return 'Hashtag'
    }
  }

  const onPressResult = useCallback((item: SearchResult) => {
    onClose()
    switch (item.type) {
      case 'contact':
        (navigation as any).navigate('Profile', {
          screen: 'DetailsGhostProfile',
          params: { profileId: item.id },
        })
        break
      case 'conversation':
        (navigation as any).navigate('Messages', {
          screen: 'Conversation',
          params: { conversationId: item.id },
        })
        break
      case 'category':
        (navigation as any).navigate('Menu', {
          screen: 'Categories',
          params: { screen: 'CategoryDetails', params: { categoryId: item.id } },
        })
        break
      case 'hashtag':
        (navigation as any).navigate('Menu', {
          screen: 'Hashtags',
        })
        break
    }
  }, [navigation, onClose])

  const renderItem = useCallback(({ item }: { item: SearchResult }) => (
    <TouchableOpacity style={styles.resultItem} onPress={() => onPressResult(item)} activeOpacity={0.7}>
      <View style={styles.resultIcon}>
        <Icon name={getIconForType(item.type)} size={20} color={theme.colors.primary} />
      </View>
      <View style={styles.resultText}>
        <Text type="bodyBold" numberOfLines={1}>{item.title}</Text>
        {item.subtitle ? (
          <Text type="bodySmall" color={theme.colors.secondary} numberOfLines={1}>{item.subtitle}</Text>
        ) : null}
      </View>
      <View style={styles.resultBadge}>
        <Text type="bodySmall" color={theme.colors.lightGrey}>{getLabelForType(item.type)}</Text>
      </View>
    </TouchableOpacity>
  ), [styles, theme, onPressResult])

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.container} activeOpacity={1} onPress={() => {}}>
          <View style={styles.searchRow}>
            <Icon name="search" color={theme.colors.lightGrey} size={20} />
            <TextInput
              ref={inputRef}
              style={styles.input}
              placeholder={t('search.placeholder')}
              placeholderTextColor={theme.colors.lightGrey}
              value={query}
              onChangeText={onChangeText}
              returnKeyType="search"
              autoCorrect={false}
            />
            {query.length > 0 && (
              <Icon name="cross" color={theme.colors.lightGrey} onPress={() => { setQuery(''); setResults([]) }} />
            )}
          </View>

          {loading && query.length >= 2 && (
            <View style={styles.statusRow}>
              <Text type="bodySmall" color={theme.colors.lightGrey}>...</Text>
            </View>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <View style={styles.statusRow}>
              <Text type="bodySmall" color={theme.colors.lightGrey}>
                {t('search.noResults')}
              </Text>
            </View>
          )}

          {results.length > 0 && (
            <FlatList
              data={results}
              keyExtractor={(item) => `${item.type}-${item.id}`}
              renderItem={renderItem}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
            />
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  )
}

const getStyle = (theme: Theme) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Platform.OS === 'web' ? 60 : 100,
  },
  container: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    overflow: 'hidden',
    maxHeight: '70%',
    ...Platform.select({
      web: {
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
      },
    }),
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey,
  },
  input: {
    flex: 1,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.fontSize.medium,
    color: theme.colors.primary,
  },
  list: {
    maxHeight: 400,
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm + 2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.grey,
  },
  resultIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.colors.grey05,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.sm,
  },
  resultText: {
    flex: 1,
    marginRight: theme.spacing.sm,
  },
  resultBadge: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: theme.colors.grey10,
  },
  statusRow: {
    padding: theme.spacing.lg,
    alignItems: 'center',
  },
})
